/**
 * @fileoverview `EventFigure` (WEB-715) — the category-resolved figure block
 * mounted between the title block and module `01` on the Event v2 spine
 * (`EventSpineTemplate`'s `figure` slot, WEB-704 / WEB-705).
 *
 * Design source: `docs/design-system/event-v2-m2-module-specs.md` §6
 * ("Figure block — `hasFigure`"), measured against the real Design Canvas
 * render. Two deliberate deviations from that mockup, both required by the
 * WEB-715 issue and called out in §6.2 of the spec:
 *
 * 1. **Real `<img>`, not a CSS background.** The mockup paints the photo as
 *    `background: url(...) 68% center / cover` on a `role="img"` `<div>`.
 *    This figure is the page's only large image and therefore its LCP
 *    element — a background-image cannot be prioritised by the preload
 *    scanner, so this renders an actual `<img loading="eager"
 *    fetchPriority="high">` with explicit `width`/`height` instead. Only the
 *    *framing* (`object-fit: cover`) carries over; the mockup's `68% center`
 *    focal bias was per-asset art direction for its own demo photo, not a
 *    global default, so this defaults to `object-position: center` absent
 *    any per-event focal-point data.
 * 2. **`<figure>` + `<figcaption>`, not a bare `<div>`.** §6.1 notes the
 *    mockup's outer element is literally a `<div>` ("not `<figure>`") and
 *    §6.3 gives the caption element as `<figcaption>`. A `<figcaption>` is
 *    only semantically meaningful inside a `<figure>` ancestor, and "Semantic
 *    HTML First" (web coding-style rules) prefers the real element over a
 *    styled `<div>` when nothing about the design depends on the choice —
 *    every measured value (radius, border, gradient stops, caption
 *    typography) is CSS and unaffected by the wrapper tag.
 *
 * ## Reuse, not a second image pipeline
 *
 * The `<img>` sources run through {@link buildEventImageSources} — which
 * itself funnels every candidate through `toDisplayImageUrl`, the same
 * render-time CSP/proxy chokepoint `EventHeroImage` uses — so provider hosts
 * (Ticketmaster / Eventbrite) route through the same-origin proxy and an
 * unproxiable host degrades to "no figure" rather than a CSP-blocked `<img>`.
 * WEB-817 added the `imageUrls` half of that: when PIP-235 variants exist the
 * figure emits a `srcset`/`sizes` pair sized to this slot, and it falls back
 * to `imageUrl` unchanged when they do not.
 * Unlike `EventHeroImage`, an image-less event renders **no** figure element
 * at all here (§6, "Degrade cases" — LOCI has no branded-placeholder
 * requirement for this slot; the whole block self-gates on `imageUrl`).
 *
 * ## Aspect ratio
 *
 * `aspect-ratio: var(--evt-fig-ar)` — the WEB-702 ladder already shipped in
 * `eventSpine.css` (`2.1/1` desktop → `2/1` tablet → `16/9` mobile). This
 * component consumes the variable and declares nothing of its own; it must
 * render as a descendant of `.event-spine` (or any wrapper that sets the
 * same custom property, as the stories here do) for the ratio to resolve.
 *
 * ## Caption content — every part self-gates (§6.4)
 *
 * `description`, `credit`, and `dimensions` are all per-event image metadata
 * LOCI does not store today (§11 "Degrade-case index") — every caller passes
 * these as `undefined` in production, and the whole `<figcaption>` renders
 * nothing when all three are absent. The ` · ` between description and
 * credit is a literal text node (per §6.4, not a border or pseudo-element)
 * and appears only when *both* are present; `dimensions` appears only when
 * BOTH supplied AND the viewport is ≥1024px — a presence gate, not a
 * visibility one (§6.4: "absent from the DOM entirely" below desktop, not
 * merely hidden). CSS alone cannot remove a DOM node per breakpoint, so this
 * is resolved with a real (SSR-safe) matchMedia check, following the same
 * "avoid hydration mismatch, render the safe/absent default until the effect
 * resolves" idiom already used by `SortMenu` (`components/molecules/SortMenu`)
 * for its own >=1024px breakpoint switch.
 *
 * ## Alt text (WEB-715 implementation note)
 *
 * The component takes no `title` prop, so it is structurally impossible for
 * the `<img>` alt to echo the event title. When `description` is present it
 * both drives the visible caption bold segment AND becomes the `<img>` alt
 * (a real description of the image content). When absent — the common case,
 * since LOCI has no per-event image metadata — the `<img>` is marked
 * decorative (`alt=""`) rather than fabricating or repeating anything.
 */
'use client';

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { buildEventImageSources } from '@/lib/image-proxy/eventImageSources';
import type { EventImageUrls } from '@/lib/image-proxy/eventImageSources';

/** ≥1024px per §6.4 / §7 of the M2 module spec ("Desktop ≥1024"). */
const FIGURE_DESKTOP_BREAKPOINT_PX = 1024;

/**
 * WEB-817 — the figure's rendered width at each tier, derived from
 * `eventSpine.css` rather than guessed:
 *
 * | Viewport   | Column                     | Figure width         |
 * | ---------- | -------------------------- | -------------------- |
 * | ≤767px     | `1fr`, `--evt-pad` 16      | `100vw - 32px`       |
 * | 768–1023px | `1fr`, `--evt-pad` 24      | `100vw - 48px`       |
 * | ≥1024px    | `1fr 400px` + 48px gap,    | `100vw - 512px`,     |
 * |            | `--evt-pad` 32             | settling at 704px    |
 * |            |                            | once the column hits |
 * |            |                            | its 1216px measure   |
 *
 * Without this the browser assumes `100vw` and fetches the widest candidate
 * on every tier — the exact waste WEB-817 exists to remove.
 */
const FIGURE_SIZES = [
  '(max-width: 767px) calc(100vw - 32px)',
  '(max-width: 1023px) calc(100vw - 48px)',
  '(max-width: 1279px) calc(100vw - 512px)',
  '704px',
].join(', ');

/**
 * Nominal intrinsic size hint for the `<img>` `width`/`height` attributes —
 * exactly the desktop `2.1/1` ratio (`--evt-fig-ar` at ≥1024px). The image is
 * absolutely positioned to fill its container (`object-fit: cover`), so the
 * true rendered box is governed by the container's `aspect-ratio` CSS, not
 * these attributes; they exist purely as the browser decode-size / CLS hint
 * the AC requires ("The `<img>` carries explicit `width`/`height`"), not as a
 * claimed real photo resolution (that claim, when we have one, is the
 * separate `dimensions` caption field below).
 */
const FIGURE_IMG_WIDTH = 2100;
const FIGURE_IMG_HEIGHT = 1000;

export interface EventFigureProps {
  /**
   * Raw `Event.imageUrl` from the event-detail payload. Normalised at render
   * time via {@link toDisplayImageUrl} (same-origin proxy / fallback), so the
   * caller passes the raw value unchanged. `null`/`undefined`/unproxiable →
   * the whole figure renders nothing (no branded placeholder — unlike
   * `EventHeroImage`, this slot simply omits itself).
   */
  imageUrl: string | null | undefined;
  /**
   * WEB-817 — raw `Event.imageUrls` (PIP-235). When two or more bands carry a
   * guaranteed width the `<img>` gets a `srcset`/`sizes` pair so this slot
   * fetches a right-sized rendition; otherwise the single best band becomes
   * the `src`. Absent → behaves exactly as before, on `imageUrl` alone.
   */
  imageUrls?: EventImageUrls | null;
  /**
   * A real description of the image's content (never the event title — see
   * file header). Drives both the caption's bold left segment and the
   * `<img>` alt text. Absent → the caption's left segment renders only the
   * credit (if any), and the `<img>` is marked decorative (`alt=""`).
   */
  description?: string | null;
  /**
   * Image attribution/credit. Renders as plain text following the
   * description (separated by a literal ` · `) when both are present, or
   * alone when only the credit is present. Absent → no separator, no credit
   * segment.
   */
  credit?: string | null;
  /**
   * Pixel dimensions string, e.g. `"3168 × 1344"`. Rendered only when BOTH
   * supplied AND the viewport is ≥1024px (§6.4) — absent from the DOM
   * entirely below that width, not merely hidden.
   */
  dimensions?: string | null;
  /** Optional additional class names on the `<figure>` root. */
  className?: string;
}

/** Non-blank-string guard — every optional caption field gates on this. */
function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * EventFigure — pure-props presentational molecule. See the file header for
 * the deliberate deviations from the mockup and the self-gating contract.
 */
export function EventFigure({
  imageUrl,
  imageUrls,
  description,
  credit,
  dimensions,
  className,
}: EventFigureProps): React.ReactElement | null {
  // WEB-817: `large` is the right band for this slot — it is the widest
  // element on the spine (up to 704px at 1x, more on retina) — and the
  // builder walks down only when no larger rendition exists.
  const imageSources = buildEventImageSources({
    imageUrl,
    imageUrls,
    sizes: FIGURE_SIZES,
    preferredBand: 'large',
  });
  const hasImage = imageSources.src.length > 0;

  // Hooks run unconditionally (Rules of Hooks) even when the component is
  // about to return null below — the effect is harmless in that case.
  //
  // `undefined` until the effect resolves client-side: SSR and the initial
  // hydration render both omit the dimensions span (the safe, mobile-first
  // default), so there is no hydration mismatch. Only after mount does a
  // real ≥1024px viewport flip this to `true` and insert the span — the same
  // trade-off `SortMenu` accepts for its own breakpoint switch. In practice
  // this flash is unobservable: LOCI has no `dimensions` data today, so the
  // span never renders in production regardless.
  const [isDesktop, setIsDesktop] = React.useState<boolean | undefined>(undefined);
  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${FIGURE_DESKTOP_BREAKPOINT_PX}px)`);
    const onChange = (): void => setIsDesktop(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (!hasImage) {
    return null;
  }

  const trimmedDescription = isNonEmptyString(description) ? description.trim() : '';
  const trimmedCredit = isNonEmptyString(credit) ? credit.trim() : '';
  const trimmedDimensions = isNonEmptyString(dimensions) ? dimensions.trim() : '';

  const hasDescription = trimmedDescription.length > 0;
  const hasCredit = trimmedCredit.length > 0;
  const hasCaptionLeft = hasDescription || hasCredit;
  const showDimensions = isDesktop === true && trimmedDimensions.length > 0;
  const hasCaption = hasCaptionLeft || showDimensions;

  // §"Alt text" above: description real content when present, otherwise
  // decorative. Never the event title — this component has no `title` prop.
  const alt = hasDescription ? trimmedDescription : '';

  return (
    <figure
      data-testid="event-figure"
      className={cn(
        'relative mt-[30px] aspect-(--evt-fig-ar) overflow-hidden rounded-ds-lg border border-ds-border bg-bg',
        className,
      )}
    >
      <img
        // WEB-817: `srcSet`/`sizes` are spread together and only ever appear
        // as a pair — an `srcset` without `sizes` falls back to `100vw`.
        {...imageSources}
        alt={alt}
        width={FIGURE_IMG_WIDTH}
        height={FIGURE_IMG_HEIGHT}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        data-testid="event-figure-image"
        className="absolute inset-0 h-full w-full object-cover"
      />

      {hasCaption && (
        <figcaption
          data-testid="event-figure-caption"
          className={cn(
            'absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 px-[18px] py-[14px]',
            'font-mono text-micro uppercase tracking-[0.14em] text-[rgba(255,255,255,0.72)]',
            'bg-[linear-gradient(180deg,transparent_0%,rgba(8,12,22,0.88)_90%)]',
          )}
        >
          {hasCaptionLeft && (
            <span data-testid="event-figure-caption-left" className="min-w-0 truncate">
              {hasDescription && (
                <b className="font-medium text-[rgba(255,255,255,0.94)]">{trimmedDescription}</b>
              )}
              {hasDescription && hasCredit && ' · '}
              {hasCredit && trimmedCredit}
            </span>
          )}
          {showDimensions && (
            <span data-testid="event-figure-dimensions" className="shrink-0">
              {trimmedDimensions}
            </span>
          )}
        </figcaption>
      )}
    </figure>
  );
}
